import fetch from 'cross-fetch'; // eslint-disable-line no-unused-vars
import { RSAA } from 'redux-api-middleware';
import i18n, { persistLocale } from '../utils/i18n';
import { push } from 'connected-react-router';
import { merge, omit, startsWith, isEmpty, trim, get, values } from 'lodash';
import { appNamedRoutes } from '../routes';
import { playersFilterClear } from './internal';
import { addMeta, tranformSuccessPayload, tranformSuccessPayloadWithHeaders, normalizeSuccessPayload, transformBodyParams, tranformEmptyPayload } from './transformers';
import { isQueryChanged, getParamsFromQuery, getQueryFromParams } from '../utils/query';
import Routes from '../utils/routes.js.erb';

export const AUTH_REQUEST = 'AUTH_REQUEST';
export const AUTH_RECEIVE = 'AUTH_SUCCESS';
export const API_FAILURE = 'API_FAILURE';

const postLogin = (params, meta = {}) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_authenticate_path(),
      headers: { 'Content-Type': 'application/json', 'JWTAUD': 'dashboard' },
      method: 'POST',
      types: [AUTH_REQUEST, tranformSuccessPayloadWithHeaders(AUTH_RECEIVE, meta), API_FAILURE],
      body: JSON.stringify(params),
      fetch: fetch,
    },
  };
}

export const authorize = (email, password) => postLogin({ email, password });

export const AUTH_REFRESH_REQUEST = 'AUTH_REFRESH_REQUEST';
export const AUTH_REFRESH_RECEIVE = 'AUTH_REFRESH_SUCCESS';

export const refreshAuth = (meta = {}) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_authentication_path(),
      headers: authHeaders(),
      method: 'GET',
      types: [AUTH_REFRESH_REQUEST, tranformSuccessPayloadWithHeaders(AUTH_REFRESH_RECEIVE, meta), API_FAILURE],
      fetch: fetch,
    },
  };
}

const AUTH_LOGOUT_REQUEST = 'AUTH_LOGOUT_REQUEST';
const AUTH_LOGOUT_RECEIVE = 'AUTH_LOGOUT_RECEIVE';

const unauthorize = () => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_logout_path(),
      headers: authHeaders(),
      method: 'DELETE',
      types: [AUTH_LOGOUT_REQUEST, tranformEmptyPayload(AUTH_LOGOUT_RECEIVE), API_FAILURE],
      fetch: fetch,
    },
  };
}

export const SIGN_OUT = 'SIGN_OUT';
export const AUTH_TOKEN = 'auth:authToken';

const resetStore = (errors) => ({
  type: SIGN_OUT,
  payload: errors,
});

export const logout = (errors) => (dispatch, getState) => {
  dispatch(unauthorize());
  Object.keys(getState().auth).forEach(key => {
    localStorage.removeItem(`auth:${key}`);
  });
  localStorage.removeItem(AUTH_TOKEN);
  window.auth_session_token = null;
  i18n.changeLanguage('en');
  dispatch(resetStore(errors));
  return dispatch(push(appNamedRoutes.login));
}

export const authHeaders = () => {
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Authorization': window.auth_session_token || localStorage.getItem(AUTH_TOKEN),
    'JWTAUD': 'dashboard',
    'X-Locale': i18n.language,
  };
}

export const SURVEYS_REQUEST = 'SURVEYS_REQUEST';
export const SURVEYS_RECEIVE = 'SURVEYS_RECEIVE';

const fetchSurveys = (params, meta = {}) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_asq_surveys_path(params),
      headers: authHeaders(),
      method: 'GET',
      types: [addMeta(SURVEYS_REQUEST, meta), tranformSuccessPayload(SURVEYS_RECEIVE, meta), API_FAILURE],
      fetch: fetch,
    },
  }
}

// Fetches surveys with parameters
export const loadSurveys = (search, meta = {}) => {
  const query = { ...getParamsFromQuery(search), init: true };
  return fetchSurveys(query, meta);
}

export const updateSurveys = (params, meta = {}) => (dispatch, getState) => {
  const state = getState();
  const query = omit({ ...state.meta.surveys, ...params }, ['q', 'totalPages']);
  if (isQueryChanged(state.meta.surveys, query, 'surveys')) {
    dispatch(fetchSurveys(query, meta));
    return dispatch(push({ search: getQueryFromParams(query) }));
  }
  return dispatch(fetchSurveys(query, meta));
}

export const SURVEYS_FILTER_REQUEST = 'SURVEYS_FILTER_REQUEST';
export const SURVEYS_FILTER_RECEIVE = 'SURVEYS_FILTER_RECEIVE';

// Fetches surveys filters
const fetchSurveysFilters = (meta) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_asq_surveys_filters_path(),
      headers: authHeaders(),
      method: 'GET',
      types: [addMeta(SURVEYS_FILTER_REQUEST, meta), addMeta(SURVEYS_FILTER_RECEIVE, meta), API_FAILURE],
      fetch: fetch,
    },
  }
}

// Fetches surveys filters
export const loadSurveysFilters = (meta = {}) => {
  return fetchSurveysFilters(meta);
}

export const PLAYERS_FILTER_REQUEST = 'PLAYERS_FILTER_REQUEST';
export const PLAYERS_FILTER_RECEIVE = 'PLAYERS_FILTER_RECEIVE';

// Fetches players filters
const fetchPlayersFilters = (params, source) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_players_filters_path(params),
      headers: authHeaders(),
      method: 'GET',
      types: [
        PLAYERS_FILTER_REQUEST,
        {
          type: PLAYERS_FILTER_RECEIVE,
          meta: merge({ source: source }, params),
        },
        API_FAILURE,
      ],
      fetch: fetch,
    },
  }
}

export const loadPlayersFilters = (value, source, available = false) => {
  if (isEmpty(trim(value))) return playersFilterClear(source);
  return (dispatch, getState) => {
    const q = source === 'modal' ? getState().modal.meta.q : getState().meta.q;
    if (!isEmpty(q) && startsWith(value, q)) return;
    const params = available ? { q: value, available } : { q: value };
    const apiCall = (dispatch) => dispatch(fetchPlayersFilters(params, source));
    apiCall.meta = {
      debounce: {
        time: 500,
        key: 'TRACK_PLAYERS_SEARCH',
      },
    };
    return apiCall(dispatch);
  }
}

export const loadPlayersFiltersByIds = (ids, source) => {
  return (dispatch, getState) => {
    const params = { ids: ids }
    const apiCall = (dispatch) => dispatch(fetchPlayersFilters(params, source));
    return apiCall(dispatch);
  }
}

export const PLAYER_FILTER_REQUEST = 'PLAYER_FILTER_REQUEST';
export const PLAYER_FILTER_RECEIVE = 'PLAYER_FILTER_RECEIVE';

// Fetches player filters
const fetchPlayerFilters = (params, meta, query = {}) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_players_filter_path(params, query),
      headers: authHeaders(),
      method: 'GET',
      types: [
        { type: PLAYER_FILTER_REQUEST, meta },
        { type: PLAYER_FILTER_RECEIVE, meta },
        API_FAILURE],
      fetch: fetch,
    },
  }
}

export const loadPlayerFilters = (value) => {
  if (!value) return playersFilterClear('modal');
  return fetchPlayerFilters(value, { id: value, source: 'modal' });
}

export const loadPlayerSurveysFilter = (params, meta) => {
  const id = get(params, 'player_id');
  return fetchPlayerFilters(id, meta, { all: true });
}

export const START_SURVEY_REQUEST = 'START_SURVEY_REQUEST';
export const START_SURVEY_RECEIVE = 'START_SURVEY_RECEIVE';

const fetchSurvey = (params, meta) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_path(...values(params)),
      headers: authHeaders(),
      method: 'GET',
      types: [
        { type: START_SURVEY_REQUEST, meta },
        normalizeSuccessPayload(START_SURVEY_RECEIVE, 'SURVEY', meta),
        API_FAILURE,
      ],
      fetch: fetch,
    },
  }
}

export const loadSurvey = (params, meta = {}) => {
  return fetchSurvey(params, meta);
}

export const PLAYER_SURVEY_REQUEST = 'PLAYER_SURVEY_REQUEST';
export const PLAYER_SURVEY_RECEIVE = 'PLAYER_SURVEY_RECEIVE';

const fetchPlayerSurvey = (id, meta) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_asq_survey_path(id),
      headers: authHeaders(),
      method: 'GET',
      types: [
        { type: PLAYER_SURVEY_REQUEST, meta },
        normalizeSuccessPayload(PLAYER_SURVEY_RECEIVE, 'PLAYER_SURVEY', meta),
        API_FAILURE,
      ],
      fetch: fetch,
    },
  }
}

export const loadPlayerSurvey = (params, meta = {}) => {
  const id = get(params, 'id');
  return fetchPlayerSurvey(id, meta);
}

export const PLAYER_REQUEST = 'PLAYER_REQUEST';
export const PLAYER_RECEIVE = 'PLAYER_RECEIVE';

const fetchPlayer = (params, meta) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_path(params),
      headers: authHeaders(),
      method: 'GET',
      types: [
        { type: PLAYER_REQUEST, meta },
        tranformSuccessPayload(PLAYER_RECEIVE, meta),
        API_FAILURE,
      ],
      fetch: fetch,
    },
  }
}

export const loadPlayer = (params, meta = {}) => {
  const id = get(params, 'player_id');
  return fetchPlayer({ id }, meta);
}

const getQuestionaireParams = (survey) => ({
  player_id: survey.player.id,
  player_questionnaire_id: survey.survey.result,
});

export const ANSWER_CREATE_REQUEST = 'ANSWER_CREATE_REQUEST';
export const ANSWER_CREATE_RECEIVE = 'ANSWER_CREATE_RECEIVE';

export const postAnswer = (pathParams, bodyParams) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_answers_path(pathParams),
      headers: authHeaders(),
      method: 'POST',
      types: [ANSWER_CREATE_REQUEST, tranformSuccessPayload(ANSWER_CREATE_RECEIVE), API_FAILURE],
      body: transformBodyParams(bodyParams),
      fetch: fetch,
    },
  };
}

export const createAnswer = (params) => (dispatch, getState) => {
  const state = getState();
  return dispatch(postAnswer(getQuestionaireParams(state.survey), params));
}

export const ANSWER_UPDATE_REQUEST = 'ANSWER_UPDATE_REQUEST';
export const ANSWER_UPDATE_RECEIVE = 'ANSWER_UPDATE_RECEIVE';

export const patchAnswer = (pathParams, bodyParams) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_answer_path(...values(pathParams)),
      headers: authHeaders(),
      method: 'PATCH',
      types: [ANSWER_UPDATE_REQUEST, tranformSuccessPayload(ANSWER_UPDATE_RECEIVE), API_FAILURE],
      body: transformBodyParams(bodyParams),
      fetch: fetch,
    },
  };
}

export const updateAnswer = (id, params) => (dispatch, getState) => {
  const state = getState();
  return dispatch(patchAnswer({ ...getQuestionaireParams(state.survey), id }, params));
}

export const ANSWER_DELETE_REQUEST = 'ANSWER_DELETE_REQUEST';
export const ANSWER_DELETE_RECEIVE = 'ANSWER_DELETE_RECEIVE';

export const destroyAnswer = (pathParams, meta) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_answer_path(...values(pathParams)),
      headers: authHeaders(),
      method: 'DELETE',
      types: [ANSWER_DELETE_REQUEST, tranformEmptyPayload(ANSWER_DELETE_RECEIVE, meta), API_FAILURE],
      fetch: fetch,
    },
  };
}

export const deleteAnswer = (id, params) => (dispatch, getState) => {
  const state = getState();
  return dispatch(destroyAnswer({ ...getQuestionaireParams(state.survey), id }, params));
}


export const SURVEY_UPDATE_REQUEST = 'SURVEY_UPDATE_REQUEST';
export const SURVEY_UPDATE_RECEIVE = 'SURVEY_UPDATE_RECEIVE';

export const patchSurvey = (pathParams, bodyParams) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_path(...values(pathParams)),
      headers: authHeaders(),
      method: 'PATCH',
      types: [SURVEY_UPDATE_REQUEST, tranformSuccessPayload(SURVEY_UPDATE_RECEIVE), API_FAILURE],
      body: transformBodyParams(bodyParams),
      fetch: fetch,
    },
  };
}

export const finishSurvey = (dateFinished = null) => (dispatch, getState) => {
  const state = getState();
  const bodyParams = { status: 'finished' };
  const date = dateFinished || state.survey.administrationDate;
  if (date) bodyParams.date_finished = date;
  return dispatch(patchSurvey(getQuestionaireParams(state.survey), bodyParams));
}


export const SURVEY_STATUS_REQUEST = 'SURVEY_STATUS_REQUEST';
export const SURVEY_STATUS_RECEIVE = 'SURVEY_STATUS_RECEIVE';

const fetchSurveyStatus = (playerId, id, jobId) => {
  return {
    [RSAA]: {
      endpoint: Routes.status_api_dashboard_player_player_questionnaire_path(playerId, id, { job_id: jobId }),
      headers: authHeaders(),
      method: 'GET',
      types: [SURVEY_STATUS_REQUEST, normalizeSuccessPayload(SURVEY_STATUS_RECEIVE, 'SURVEY'), API_FAILURE],
      fetch: fetch,
    },
  };
}

export const surveyStatus = (playerId, id, jobId) => {
  return fetchSurveyStatus(playerId, id, jobId);
}

export const WEBNOTES_REQUEST = 'WEBNOTES_REQUEST';
export const WEBNOTES_RECEIVE = 'WEBNOTES_RECEIVE';

export const fetchNotes = (params, meta, query) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_notes_path(params.playerId, params.playerQuestionnaireId, query),
      headers: authHeaders(),
      method: 'GET',
      types: [addMeta(WEBNOTES_REQUEST, meta), normalizeSuccessPayload(WEBNOTES_RECEIVE, 'WEB_NOTES', meta), API_FAILURE],
      fetch: fetch,
    },
  };
}

export const loadNotes = (params, meta = {}, query = {}) => {
  return fetchNotes(params, meta, query);
}

export const WEBNOTE_UPDATE_REQUEST = 'WEBNOTE_UPDATE_REQUEST';
export const WEBNOTE_UPDATE_RECEIVE = 'WEBNOTE_UPDATE_RECEIVE';

export const patchNote = (id, params, bodyParams) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_note_path(params.playerId, params.playerQuestionnaireId, id),
      headers: authHeaders(),
      method: 'PATCH',
      types: [WEBNOTE_UPDATE_REQUEST, tranformSuccessPayload(WEBNOTE_UPDATE_RECEIVE), API_FAILURE],
      body: transformBodyParams(bodyParams),
      fetch: fetch,
    },
  };
}

export const updateNote = (id, params, body) => {
  const bodyParams = { body, asqQuestionId: params.asqQuestionId };
  return patchNote(id, params, bodyParams);
}

export const WEBNOTE_DELETE_REQUEST = 'WEBNOTE_DELETE_REQUEST';
export const WEBNOTE_DELETE_RECEIVE = 'WEBNOTE_DELETE_RECEIVE';

export const destroyNote = (id, params, meta) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_note_path(params.playerId, params.playerQuestionnaireId, id),
      headers: authHeaders(),
      method: 'DELETE',
      types: [WEBNOTE_DELETE_REQUEST, tranformEmptyPayload(WEBNOTE_DELETE_RECEIVE, meta), API_FAILURE],
      fetch: fetch,
    },
  };
}

export const deleteNote = (id, params) => {
  const { asqQuestionId, playerQuestionnaireId } = params;
  return destroyNote(id, params, { id, asqQuestionId, playerQuestionnaireId });
}

export const WEBNOTE_CREATE_REQUEST = 'WEBNOTE_CREATE_REQUEST';
export const WEBNOTE_CREATE_RECEIVE = 'WEBNOTE_CREATE_RECEIVE';

export const postNote = (params, bodyParams) => {
  return {
    [RSAA]: {
      endpoint: Routes.api_dashboard_player_player_questionnaire_notes_path(params.playerId, params.playerQuestionnaireId),
      headers: authHeaders(),
      method: 'POST',
      types: [WEBNOTE_CREATE_REQUEST, tranformSuccessPayload(WEBNOTE_CREATE_RECEIVE, params), API_FAILURE],
      body: transformBodyParams(bodyParams),
      fetch: fetch,
    },
  };
}

export const createNote = (params, body) => {
  const bodyParams = { body, asqQuestionId: params.asqQuestionId };
  return postNote(params, bodyParams);
}

export const LOCALE_UPDATE_REQUEST = 'LOCALE_UPDATE_REQUEST';
export const LOCALE_UPDATE_RECEIVE = 'LOCALE_UPDATE_RECEIVE';

// `previousLocale` is captured by the caller before the optimistic UI switch
// so we can roll the i18n language back if the PATCH fails.
export const saveLocale = (locale, previousLocale) => ({
  [RSAA]: {
    endpoint: Routes.locale_api_dashboard_settings_path(),
    headers: authHeaders(),
    method: 'PATCH',
    types: [
      LOCALE_UPDATE_REQUEST,
      {
        type: LOCALE_UPDATE_RECEIVE,
        payload: () => {
          persistLocale(locale);
          window.location.reload();
          return { locale };
        },
      },
      {
        type: API_FAILURE,
        payload: (_action, _state, res) => {
          if (previousLocale && previousLocale !== locale) {
            i18n.changeLanguage(previousLocale);
          }
          return res;
        },
      },
    ],
    body: JSON.stringify({ locale }),
    fetch: fetch,
  },
});
